import { useState, useEffect, lazy, Suspense, memo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { TedRobot } from '../components/TedChat';
import AiMatchingLoader from '../components/AiMatchingLoader';
import LearningModesSection from '../components/landing/LearningModesSection';
import TrustSafetySection from '../components/landing/TrustSafetySection';
import RoleCardsSection from '../components/landing/RoleCardsSection';
import FinalCTASection from '../components/landing/FinalCTASection';
import BrandReveal from '../components/landing/BrandReveal';
import SuiteSwitcher from '../components/SuiteSwitcher';
import LanguageMenu from '../components/LanguageMenu';
import EcosystemBanner from '../components/landing/EcosystemBanner';
import FeaturedLessons from '../components/landing/FeaturedLessons';
import EcosystemBridge from '../components/landing/EcosystemBridge';
import RoleJourney from '../components/landing/RoleJourney';
import QuickRegionMenu from '../components/QuickRegionMenu';
import AppFooter from '../components/AppFooter';
import FeaturedTeachersSection from '../components/landing/FeaturedTeachersSection';
import SmartFilterToggle from '../components/landing/SmartFilterToggle';
import MissionBadge from '../components/nasa/MissionBadge';
import ReadOut from '../components/nasa/ReadOut';
import ThemeSwitch from '../components/ds/ThemeSwitch';
import { 
  GraduationCap, Users, Video, MapPin, Shield, BookOpen,
  Clock, CheckCircle, ArrowLeft, ArrowRight, Globe, Play, X,
  AlertTriangle, Info, Gift, MessageCircle, MessageSquare, Star,
  Monitor, Atom, Languages, FlaskConical, Cpu, Dna, PenTool, Zap,
  Sun, Moon, ChevronDown, ShieldCheck, Menu
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TutorialVideoModal = lazy(() => import('../components/TutorialVideoModal'));
const WaitlistDialog = lazy(() => import('../components/WaitlistDialog'));
const CinematicTeaser = lazy(() => import('../components/CinematicTeaser'));

/* ────────────────────────────────────────────
   Animated Section Wrapper
   ──────────────────────────────────────────── */
const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

/* ────────────────────────────────────────────
   Stagger Container
   ──────────────────────────────────────────── */
const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.1 } }
};
const staggerItem = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language, toggleLanguage, t, localize } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const [showTutorial, setShowTutorial] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [showTeaser, setShowTeaser] = useState(false);
  const [waitlistInitialCountry, setWaitlistInitialCountry] = useState(null);
  const [showAiLoader, setShowAiLoader] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isAr = language === 'ar';

  useEffect(() => {
    fetchAnnouncements();
    const dismissed = JSON.parse(localStorage.getItem('dismissedAnnouncements') || '[]');
    setDismissedAnnouncements(dismissed);
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/announcements/active`);
      setAnnouncements(response.data.filter(a => a.show_on_landing));
    } catch { }
  };

  const dismissAnnouncement = (id) => {
    const newDismissed = [...dismissedAnnouncements, id];
    setDismissedAnnouncements(newDismissed);
    localStorage.setItem('dismissedAnnouncements', JSON.stringify(newDismissed));
  };

  const visibleAnnouncements = announcements.filter(a => !dismissedAnnouncements.includes(a.id));

  const handleGetStarted = () => {
    if (user) {
      const routes = { student: '/student', teacher: '/teacher', admin: '/admin', parent: '/parent' };
      navigate(routes[user.role] || '/');
    } else {
      navigate('/register');
    }
  };

  const features = [
    { icon: <Video className="w-6 h-6" />, title: localize({ ar: 'دروس عن بعد', en: 'Online Lessons' }), desc: localize({ ar: 'فصول افتراضية بالفيديو والسبورة التفاعلية', en: 'Virtual classrooms with video & interactive whiteboard' }) },
    { icon: <MapPin className="w-6 h-6" />, title: localize({ ar: 'دروس حضورية', en: 'In-Person' }), desc: localize({ ar: 'مدرسون في جميع الإمارات السبع', en: 'Tutors across all 7 emirates' }) },
    { icon: <Shield className="w-6 h-6" />, title: localize({ ar: 'بيئة آمنة', en: 'Safe Environment' }), desc: localize({ ar: 'مراقبة صامتة لأولياء الأمور أثناء الدرس', en: 'Silent monitoring for parents during lessons' }) },
    { icon: <Clock className="w-6 h-6" />, title: localize({ ar: 'حجز فوري', en: 'Instant Booking' }), desc: localize({ ar: 'احجز في ثوانٍ واختر الوقت المناسب لك', en: 'Book in seconds, pick a time that suits you' }) }
  ];

  const subjects = [
    { name: localize({ ar: 'الرياضيات', en: 'Mathematics' }), icon: <Monitor className="w-5 h-5" /> },
    { name: localize({ ar: 'الفيزياء', en: 'Physics' }), icon: <Zap className="w-5 h-5" /> },
    { name: localize({ ar: 'الكيمياء', en: 'Chemistry' }), icon: <FlaskConical className="w-5 h-5" /> },
    { name: localize({ ar: 'اللغة العربية', en: 'Arabic' }), icon: <PenTool className="w-5 h-5" /> },
    { name: localize({ ar: 'اللغة الإنجليزية', en: 'English' }), icon: <Languages className="w-5 h-5" /> },
    { name: localize({ ar: 'العلوم', en: 'Science' }), icon: <Atom className="w-5 h-5" /> },
    { name: localize({ ar: 'الحاسوب', en: 'Computer' }), icon: <Cpu className="w-5 h-5" /> },
    { name: localize({ ar: 'الأحياء', en: 'Biology' }), icon: <Dna className="w-5 h-5" /> }
  ];

  const emirates = [
    localize({ ar: 'دبي', en: 'Dubai' }),
    localize({ ar: 'أبوظبي', en: 'Abu Dhabi' }),
    localize({ ar: 'الشارقة', en: 'Sharjah' }),
    localize({ ar: 'عجمان', en: 'Ajman' }),
    localize({ ar: 'رأس الخيمة', en: 'RAK' }),
    localize({ ar: 'الفجيرة', en: 'Fujairah' }),
    localize({ ar: 'أم القيوين', en: 'UAQ' })
  ];

  return (
    <div className={`min-h-screen overflow-x-hidden bg-transparent ${isAr ? 'rtl' : 'ltr'}`} dir={isAr ? 'rtl' : 'ltr'}>
      {/* Premium brand reveal — once per session, ~1.4s */}
      <BrandReveal />
      {/* Announcements — hidden on landing for competition showcase. To re-enable, set SHOW_LANDING_ANNOUNCEMENTS=true. */}
      {false && visibleAnnouncements.length > 0 && (
        <div className="fixed top-[28px] left-0 right-0 z-[60]">
          {visibleAnnouncements.slice(0, 1).map((a) => (
            <div key={a.id} className={`${a.type === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-800' : a.type === 'success' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-slate-50 border-slate-200 text-slate-800'} border-b px-4 py-3`}>
              <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1">
                  {a.type === 'warning' ? <AlertTriangle className="w-5 h-5" /> : <Info className="w-5 h-5" />}
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{isAr ? a.title_ar : (a.title_en || a.title_ar)}</p>
                    <p className="text-xs opacity-80">{isAr ? a.content_ar : (a.content_en || a.content_ar)}</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => dismissAnnouncement(a.id)}><X className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ═══════════ NAVBAR (Deep Navy) ═══════════ */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-navy-800/95 border-b border-white/5 transition-all">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-2">
          <motion.div 
            initial={{ opacity: 0, x: isAr ? 20 : -20 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ duration: 0.5 }}
            className="flex items-center gap-2 cursor-pointer shrink-0" 
            onClick={() => navigate('/')}
          >
            <img src="/lwt-icon-new.png" alt="LWT" className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg" loading="eager" />
            <span className="brand-wordmark text-base hidden sm:block text-white" data-testid="navbar-brand-wordmark">Learn With Trust</span>
          </motion.div>

          {/* ── MOBILE: compact UAE pill + Globe + Hamburger (always visible) ── */}
          <div className="md:hidden flex items-center gap-1.5">
            {/* Mini UAE flag pill */}
            <span
              className="inline-flex items-center gap-1 h-7 px-1.5 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-[10px] font-bold shadow-[0_3px_12px_rgba(99,102,241,0.4)]"
              data-testid="mobile-uae-pill"
              aria-label={localize({ ar: 'الإمارات', en: 'UAE' })}
            >
              <img
                src="https://flagcdn.com/w40/ae.png"
                srcSet="https://flagcdn.com/w40/ae.png 1x, https://flagcdn.com/w80/ae.png 2x"
                width="18"
                height="13"
                alt={localize({ ar: 'علم الإمارات', en: 'UAE flag' })}
                className="h-[13px] w-[18px] rounded-[2px] object-cover ring-1 ring-white/30 shrink-0"
                loading="eager"
              />
              <span className="relative flex h-1.5 w-1.5 me-0.5" aria-hidden>
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-80" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-300" />
              </span>
            </span>

            {/* Compact glowing globe → triggers waitlist directly */}
            <button
              type="button"
              onClick={() => { setWaitlistInitialCountry(null); setShowWaitlist(true); }}
              className="relative inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/[0.06] hover:bg-white/[0.12] ring-1 ring-white/15 transition-colors"
              aria-label={localize({ ar: 'خارج الإمارات', en: 'Outside UAE' })}
              data-testid="mobile-globe-btn"
            >
              <span
                className="absolute inset-0 rounded-full bg-indigo-400/40 blur-md motion-reduce:hidden"
                aria-hidden
                style={{ animation: 'lwt-globe-pulse 2.6s ease-in-out infinite' }}
              />
              <Globe className="relative w-4 h-4 text-indigo-200" strokeWidth={2.4} />
            </button>

            {/* Hamburger */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/[0.06] hover:bg-white/[0.12] ring-1 ring-white/15 text-white transition-colors"
              aria-label={localize({ ar: 'القائمة', en: 'Menu' })}
              aria-expanded={mobileMenuOpen}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>

          {/* ── DESKTOP: full toolbar ── */}
          <motion.div 
            initial={{ opacity: 0, x: isAr ? -20 : 20 }} 
            animate={{ opacity: 1, x: 0 }} 
            transition={{ duration: 0.5 }}
            className="hidden md:flex items-center gap-2"
          >
            <Button variant="ghost" size="sm" onClick={toggleTheme} className="hidden">{/* legacy hidden — replaced by ThemeSwitch */}</Button>
            <ThemeSwitch size="sm" data-testid="landing-theme-toggle" />
            <LanguageMenu compact />

            {/* Suite Switcher (LWT / TD / TVD) */}
            <div className="hidden sm:block">
              <SuiteSwitcher language={language} current="lwt" compact />
            </div>

            {/* Location pair: Inside UAE pill + Outside UAE dropdown */}
            <div className="flex items-center gap-1.5 p-1 rounded-full bg-white/[0.04] border border-white/10">
              {/* Inside UAE — active indicator with inline LIVE pulse + real flag image */}
              <span
                className="inline-flex items-center gap-2 h-7 ps-1.5 pe-3 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-[11px] font-bold shadow-[0_4px_18px_rgba(99,102,241,0.45)] cursor-default"
                data-testid="inside-uae-pill"
                title={localize({ ar: 'إصدار الإمارات (نشط) — يعمل الآن', en: 'UAE Version (active) — Live now' })}
              >
                {/* Real UAE flag image (emoji can be missing on Windows/Linux fonts) */}
                <img
                  src="https://flagcdn.com/w40/ae.png"
                  srcSet="https://flagcdn.com/w40/ae.png 1x, https://flagcdn.com/w80/ae.png 2x"
                  width="20"
                  height="14"
                  alt={localize({ ar: 'علم الإمارات', en: 'UAE flag' })}
                  className="h-[14px] w-[20px] rounded-[2px] object-cover ring-1 ring-white/30 shrink-0"
                  data-testid="uae-flag-img"
                  loading="eager"
                />
                <span>{localize({ ar: 'الإمارات', en: 'UAE' })}</span>
                {/* Inline LIVE indicator — attached to the pill (no more floating overlay) */}
                <span
                  className="inline-flex items-center gap-1 ps-1.5 -me-1 border-s border-white/25 text-[9px] font-black tracking-[0.14em] uppercase text-emerald-200"
                  data-testid="live-in-uae-badge"
                >
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-80" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-300" />
                  </span>
                  {localize({ ar: 'مباشر', en: 'Live' })}
                </span>
              </span>
              {/* Outside UAE — switcher (with glowing globe) */}
              <QuickRegionMenu
                language={language}
                onPickCountry={(c) => {
                  setWaitlistInitialCountry(c);
                  setShowWaitlist(true);
                }}
              />
            </div>

            {/* ── Primary section links (desktop only) ───────────────
                Lightweight in-page anchors for quick scanning. They
                deliberately scroll instead of route-changing so we
                keep the single-page lead-gen flow intact. */}
            <nav className="hidden lg:flex items-center gap-1 me-2" data-testid="hero-nav-links" aria-label="Primary">
              {[
                { href: '#role-cards',      labelAr: 'للطلاب',          labelEn: 'Students' },
                { href: '#role-cards',      labelAr: 'للمعلمين',        labelEn: 'Tutors' },
                { href: '#role-cards',      labelAr: 'لأولياء الأمور', labelEn: 'Parents' },
                { href: '/free',            labelAr: 'الأسعار',         labelEn: 'Pricing',  route: true },
                { href: '/contact',         labelAr: 'تواصل',           labelEn: 'Contact',  route: true },
              ].map((it, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => {
                    if (it.route) { navigate(it.href); return; }
                    document.querySelector(it.href)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="px-2.5 py-1.5 text-[12.5px] font-semibold rounded-md text-slate-300 hover:text-white hover:bg-white/[0.06] transition-colors"
                  data-testid={`nav-link-${(it.labelEn||'').toLowerCase()}`}
                >
                  {localize({ ar: it.labelAr, en: it.labelEn })}
                </button>
              ))}
            </nav>

            {user ? (
              <Button onClick={handleGetStarted} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-6 h-9 text-xs font-semibold" data-testid="dashboard-btn">{t('dashboard')}</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => navigate('/login')} className="text-slate-200 hover:text-white hover:bg-white/10 text-xs h-9 font-medium" data-testid="login-btn">{t('login')}</Button>
                <Button onClick={() => navigate('/register')} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-6 h-9 text-xs font-semibold shadow-[0_4px_20px_rgba(79,70,229,0.35)]" data-testid="register-btn">{t('register')}</Button>
              </>
            )}
          </motion.div>
        </div>

        {/* ── MOBILE DRAWER ── */}
        {mobileMenuOpen && (
          <div
            className="md:hidden border-t border-white/10 bg-[rgba(13,17,23,0.95)] backdrop-blur-2xl"
            data-testid="mobile-menu-drawer"
          >
            <div className="px-4 py-3 space-y-2">
              {user ? (
                <Button onClick={() => { setMobileMenuOpen(false); handleGetStarted(); }} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-10 text-sm font-semibold" data-testid="mobile-dashboard-btn">
                  {t('dashboard')}
                </Button>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="ghost" onClick={() => { setMobileMenuOpen(false); navigate('/login'); }} className="bg-white/[0.06] text-white hover:bg-white/[0.12] rounded-xl h-10 text-sm font-semibold" data-testid="mobile-login-btn">
                    {t('login')}
                  </Button>
                  <Button onClick={() => { setMobileMenuOpen(false); navigate('/register'); }} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl h-10 text-sm font-semibold shadow-[0_4px_18px_rgba(79,70,229,0.4)]" data-testid="mobile-register-btn">
                    {t('register')}
                  </Button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 pt-1">
                <div className="col-span-1">
                  <LanguageMenu />
                </div>
                <div className="inline-flex items-center justify-center h-10 rounded-xl bg-white/[0.04] ring-1 ring-white/10">
                  <ThemeSwitch data-testid="mobile-theme-toggle" />
                </div>
              </div>
              <div className="pt-2">
                <SuiteSwitcher language={language} current="lwt" compact />
              </div>
            </div>
          </div>
        )}
      </nav>

      <main id="main-content" data-testid="landing-main">
      {/* ═══════════ HERO — Clean Apple/Linear minimalism ═══════════
          Backgrounds removed per user request. The hero now relies on
          pure typography + the Electric-Blue CTA over a subtle
          token-driven surface gradient. No image, no cosmic stage. */}
      <section className="relative overflow-hidden border-b border-[var(--border-color)] bg-gradient-to-b from-[var(--bg-primary)] via-[var(--bg-secondary)] to-[var(--bg-primary)]">
        {/* Subtle instrumentation dot grid — keeps the NASA touch without clutter */}
        <div
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: 'radial-gradient(circle, var(--text-tertiary) 1px, transparent 1px)', backgroundSize: '40px 40px' }}
          aria-hidden
        />

        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 pb-8 sm:pt-24 sm:pb-12 relative z-10 w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Text */}
            <div className="space-y-7">
              {/* Mission slug — NASA-style readout */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
                className="flex items-center gap-3"
              >
                <MissionBadge tone="mission" data-testid="hero-mission-badge">
                  <span data-testid="free-period-mission-text" onClick={() => navigate('/free')} className="cursor-pointer">
                    {localize({ ar: '🎁 عرض خاص · مجاني 100% حتى سبتمبر 2026', en: '🎁 LIMITED OFFER · 100% FREE UNTIL SEP 2026' })}
                  </span>
                </MissionBadge>
                <span className="nasa-rule flex-1 max-w-[160px] hidden sm:block" aria-hidden />
              </motion.div>

              <motion.h1 
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                className="nasa-display text-3xl sm:text-5xl lg:text-[3.25rem] text-text-primary"
                style={{ lineHeight: 1.04 }}
                data-testid="hero-h1"
              >
                <ReadOut as="span" tone="accent" className="block mb-3" data-testid="hero-brand-wordmark">
                  LEARN&nbsp;·&nbsp;WITH&nbsp;·&nbsp;TRUST
                </ReadOut>
                <span className="block">{localize({ ar: 'تعلَّم بثقة', en: 'Learn with confidence' })}</span>
                <span
                  className="block text-accent-primary nasa-mono"
                  style={{ marginTop: '0.22em', fontWeight: 700, letterSpacing: '-0.01em' }}
                >
                  {localize({ ar: 'مع الذكاء الاصطناعي.', en: 'powered by AI.' })}
                </span>
              </motion.h1>

              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="text-text-secondary text-sm sm:text-base leading-relaxed max-w-lg"
              >
                {localize({
                  ar: 'دروس موثوقة · معلّمون متحقَّق منهم · أدوات ذكاء اصطناعي — في كل إمارات الدولة.',
                  en: 'Trusted lessons · Verified tutors · AI tools — across all 7 UAE Emirates.'
                })}
              </motion.p>

              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="flex flex-wrap items-center gap-4 pt-2"
              >
                {/* PRIMARY — Electric-Blue, instrumented, no idle wobble.
                    Calm & confident per the Apple/Linear/Stripe direction. */}
                <div className="relative inline-flex group">
                  {/* Calm always-on pulse halo so the "instant lesson" CTA gently invites the eye. */}
                  <span
                    className="absolute -inset-1 rounded-full bg-[var(--accent-primary)]/25 blur-xl opacity-60 group-hover:opacity-100 transition-opacity duration-300 animate-pulse"
                    aria-hidden
                  />

                  <Button
                    onClick={() => setShowAiLoader(true)}
                    className="relative z-10 bg-[var(--accent-primary)] hover:bg-[var(--accent-primary-hover)] text-white rounded-full px-6 sm:px-9 h-12 sm:h-13 text-sm sm:text-base font-semibold border border-white/10 shadow-[0_4px_14px_rgba(37,99,235,0.32)] hover:shadow-[0_8px_24px_rgba(37,99,235,0.45)] transition-all duration-200 ease-out hover:-translate-y-px overflow-hidden lwt-cta-breathe"
                    data-testid="available-tutors-btn"
                  >
                    <span className="relative z-10 flex items-center gap-2 sm:gap-2.5">
                      <span className="relative inline-flex items-center">
                        <span className="h-2 w-2 rounded-full bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)]" />
                      </span>
                      <Zap className="w-4 h-4 sm:w-5 sm:h-5 fill-white" />
                      {localize({ ar: 'ابدأ درس الآن', en: 'Start a Lesson Now' })}
                    </span>
                    {/* Calm shimmer on hover only */}
                    <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-[1100ms] ease-out bg-gradient-to-r from-transparent via-white/15 to-transparent" aria-hidden />
                  </Button>
                </div>

                {/* SECONDARY CTA — bold, equal-weight to primary, leads to public tutor directory */}
                <button
                  type="button"
                  onClick={() => navigate('/tutors')}
                  className="group inline-flex items-center gap-2 px-6 sm:px-7 h-12 sm:h-13 rounded-full text-sm sm:text-base font-semibold transition-all duration-200 ring-1 ring-emerald-400/60 text-emerald-700 dark:text-emerald-300 bg-emerald-50/80 hover:bg-emerald-100 dark:bg-emerald-500/[0.08] dark:hover:bg-emerald-500/[0.16] hover:-translate-y-px"
                  data-testid="hero-cta-btn"
                >
                  <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                  {localize({ ar: 'تصفّح المعلمين', en: 'Browse tutors' })}
                  <ArrowRight className={`w-3.5 h-3.5 transition-transform group-hover:translate-x-1 ${isAr ? 'rotate-180 group-hover:-translate-x-1' : ''}`} />
                </button>

                {/* TERTIARY — Cinematic 90-sec ecosystem teaser */}
                <button
                  type="button"
                  onClick={() => setShowTeaser(true)}
                  className="group inline-flex items-center gap-2 px-3.5 h-9 rounded-full text-xs font-bold tracking-wide transition-all bg-[var(--bg-elevated)] hover:bg-[var(--bg-elevated-hover)] ring-1 ring-[var(--border-color)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] backdrop-blur-sm"
                  data-testid="hero-watch-teaser-btn"
                >
                  <span className="relative flex items-center justify-center w-5 h-5 rounded-full bg-rose-500/90 group-hover:bg-rose-400 transition shadow-[0_0_10px_rgba(244,63,94,0.6)]">
                    <Play className="w-2.5 h-2.5 text-white fill-white" />
                  </span>
                  {localize({ ar: 'شاهد قصتنا في 90 ثانية', en: 'Watch our 90-sec story' })}
                </button>
              </motion.div>

              {/* Trust badges — NASA telemetry rail */}
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-2"
                data-testid="hero-telemetry-rail"
              >
                <div className="nasa-field nasa-panel px-3 py-2.5">
                  <ReadOut tone="mission" className="!text-[9px]">CHILD · SAFE</ReadOut>
                  <span className="nasa-mono text-xs text-[var(--text-primary)]">{localize({ ar: 'بيئة آمنة', en: 'Verified env.' })}</span>
                </div>
                <div className="nasa-field nasa-panel px-3 py-2.5">
                  <ReadOut tone="mission" className="!text-[9px]">TUTORS · OK</ReadOut>
                  <span className="nasa-mono text-xs text-[var(--text-primary)]">{localize({ ar: 'موثوقون', en: 'Verified' })}</span>
                </div>
                <div className="nasa-field nasa-panel px-3 py-2.5">
                  <ReadOut tone="accent" className="!text-[9px]">COVERAGE · UAE</ReadOut>
                  <span className="nasa-mono text-xs text-[var(--text-primary)]">{localize({ ar: '7/7 إمارات', en: '7/7 emirates' })}</span>
                </div>
                <div className="nasa-field nasa-panel px-3 py-2.5">
                  <ReadOut tone="accent" className="!text-[9px]">AI · SHIELD</ReadOut>
                  <span className="nasa-mono text-xs text-[var(--text-primary)]">{localize({ ar: 'حماية AI', en: 'AI protected' })}</span>
                </div>
              </motion.div>
            </div>

            {/* Right: Logo + TED — NASA instrument panels */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="hidden lg:flex flex-col items-center gap-6"
            >
              <div className="relative nasa-panel nasa-panel--cornered p-12 w-full max-w-sm">
                {/* Cosmic indigo glow */}
                <div className="absolute -inset-1 bg-gradient-to-br from-indigo-400/15 via-violet-500/8 to-emerald-400/10 rounded-md blur-2xl pointer-events-none" />
                <img src="/lwt-logo-new.png" alt="Learn With Trust" className="relative z-10 w-40 h-40 object-contain mx-auto" loading="eager" />
              </div>
              {/* TED Card — clickable: opens the floating TED chat via custom event */}
              <motion.button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('lwt:open-ted'))}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                whileHover={{ scale: 1.015 }}
                whileTap={{ scale: 0.985 }}
                className="nasa-panel nasa-panel--cornered p-5 max-w-xs cursor-pointer text-start hover:ring-1 hover:ring-indigo-400/40 transition-shadow"
                data-testid="hero-ted-card"
                aria-label={localize({ ar: 'افتح محادثة TED', en: 'Open TED chat' })}
              >
                <div className="flex items-center justify-between mb-3">
                  <ReadOut tone="mission">TED · UPLINK</ReadOut>
                  <MissionBadge tone="mission" className="!py-0.5 !px-1.5 !text-[8px] !tracking-[0.16em]">ONLINE</MissionBadge>
                </div>
                <TedRobot size={48} animate={true} className="mx-auto mb-3" />
                <h2 className="nasa-display text-base mb-1.5 text-center text-[var(--text-primary)]">
                  {localize({ ar: 'مساعدك الذكي TED', en: 'Meet TED' })}
                </h2>
                <p className="text-[11px] leading-relaxed mb-3 text-center nasa-mono text-[var(--text-secondary)]">
                  {localize({ ar: 'اسأله بأي لغة عن المنصة', en: 'Ask anything in any language' })}
                </p>
                <div className="nasa-rule mb-3" />
                <div className="inline-flex items-center gap-1.5 text-indigo-500 dark:text-indigo-300 text-[10px] font-semibold w-full justify-center">
                  <MessageCircle className="w-3.5 h-3.5" />
                  {localize({ ar: 'اضغط للمحادثة الآن', en: 'Click to chat now' })}
                </div>
              </motion.button>
            </motion.div>
          </div>

          {/* Scroll indicator */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
          >
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <ChevronDown className="w-5 h-5 text-white/40" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════ ROLE CARDS — "Pick your role" (new Feb 2026) ═══════════ */}
      <RoleCardsSection isDark={isDark} />

      {/* ═══════════ FEATURED LESSONS (top of funnel — Start a Lesson) ═══════════ */}
      <FeaturedLessons language={language} isDark={isDark} />

      {/* ═══════════ ECOSYSTEM BRIDGE — Creative Writing → TD/TVD via SSO ═══════════ */}
      <Suspense fallback={null}>
        <EcosystemBridge language={language} source="creative-writing-section" />
      </Suspense>

      {/* ═══════════ ROLE JOURNEY (How to use — Student / Parent / Teacher) ═══════════ */}
      <RoleJourney language={language} isDark={isDark} />

      {/* ═══════════ LEARNING MODES ═══════════ */}
      <LearningModesSection language={language} isDark={isDark} />

      {/* ═══════════ SMART MODE FILTER ═══════════ */}
      <SmartFilterToggle language={language} isDark={isDark} />

      {/* ═══════════ FEATURED TEACHERS ═══════════ */}
      <FeaturedTeachersSection language={language} isDark={isDark} />

      {/* ═══════════ TRUST & SAFETY ═══════════ */}
      <TrustSafetySection language={language} isDark={isDark} />

      {/* ═══════════ FINAL CTA ═══════════ */}
      <FinalCTASection language={language} onStartInstant={() => setShowAiLoader(true)} />

      {/* ═══════════ ECOSYSTEM BANNER (TD / TVD) ═══════════ */}
      <EcosystemBanner language={language} isDark={isDark} />

      </main>

      {/* ═══════════ FOOTER ═══════════ */}
      <AppFooter language={language} variant="full" />

      <Suspense fallback={null}>
        {showTutorial && <TutorialVideoModal isOpen={showTutorial} onClose={() => setShowTutorial(false)} />}
        {showWaitlist && <WaitlistDialog open={showWaitlist} onClose={() => { setShowWaitlist(false); setWaitlistInitialCountry(null); }} language={language} initialCountry={waitlistInitialCountry} />}
        {showTeaser && <CinematicTeaser open={showTeaser} onClose={() => setShowTeaser(false)} />}
        <AiMatchingLoader
          open={showAiLoader}
          language={language}
          onFinish={() => {
            setShowAiLoader(false);
            navigate('/student/search?available=true');
          }}
        />
      </Suspense>
    </div>
  );
};

export default memo(LandingPage);
